import * as requests from "@/API/requests";

// Monotonic counter shared across all loadGoals dispatches. When goal toggles
// fire in rapid succession we end up with overlapping in-flight loadGoals
// requests; without sequencing, whichever resolves last wins — and that may be
// older data. We tag each dispatch with a sequence and drop the commit if a
// newer dispatch has already started, so the most recent intent always wins.
let nextPlanLoadSeq = 0;

const goalsStore = {
  namespaced: true,

  state: () => ({
    plan: {
      goals_ef: {},
      goals_ff: {},
      goals_cf: {},
    },
    dashboardGoals: {},
  }),
  mutations: {
    setPlan(state, payload) {
      state.plan = payload;
    },
    setDashboardGoals(state, payload) {
      state.dashboardGoals = payload;
    },
  },
  actions: {
    async loadGoals(store) {
      const mySeq = ++nextPlanLoadSeq;
      const response = await requests.loadGoalsRequest();
      // A newer loadGoals was dispatched while we were awaiting — its commit
      // is the source of truth, drop ours so we don't overwrite it with
      // potentially-stale data.
      if (mySeq < nextPlanLoadSeq) return response;
      store.commit("setPlan", response);
      return response;
    },
    async loadSwapData() {
      return await requests.loadSwapDataRequest();
    },
    async transferFunds(_, payload) {
      // payload: { source_goal_id, amount, allocations: [{ id, amount }] }
      return await requests.transferFundsRequest(payload);
    },
    async goalCalculator(store, payload) {
      return await requests.goalCalculatorRequest(payload);
    },

    async goalCalculatorUpdate(store, payload) {
      return await requests.goalCalculatorUpdateRequest(payload);
    },
    async emergencyFund(store, payload) {
      return await requests.emergencyFundRequest(payload);
    },
    async emergencyFundUpdate(store, payload) {
      return await requests.emergencyFundUpdateRequest(payload);
    },
    async updateFinance(store, payload) {
      return await requests.updateFinanceRequest(payload);
    },
    async updateFinancialFreedom(store, payload) {
      return await requests.financialFreedomUpdateRequest(payload);
    },
    async updateFinancialFreedomAllocation(store, payload) {
      return await requests.updateFinancialFreedomAllocationRequest(payload);
    },
    async calculateWishlistNper(store, payload) {
      return await requests.wishlistNperRequest(payload);
    },
    async addWishlist(store, payload) {
      return await requests.wishlistAddRequest(payload);
    },
    async deleteGoal(store, payload) {
      return await requests.goalDeleteRequest(payload);
    },
    async resetPlan() {
      return await requests.resetPlanRequest();
    },
    async activateGoal(store, payload) {
      return await requests.goalActivateRequest(payload);
    },
    async getMultiAssetGoals(store, payload) {
      return await requests.multiAssetGoalsRequest(payload);
    },

    async getGoalData(store, payload) {
      return await requests.getGoalDataRequest(payload);
    },

    async calculateWithdrawal(store, payload) {
      return await requests.calculateWithdrawalRequest(payload);
    },
    async requestWithdrawal(store, payload) {
      return await requests.requestWithdrawalRequest(payload);
    },

    async loadDashboardGoals(store) {
      const response = await requests.loadDashboardGoalsRequest();
      store.commit("setDashboardGoals", response);
      return response;
    },
    async loadWithdrawal() {
      return await requests.loadWithdrawalRequest();
    },
    async getGoalTypes() {
      return await requests.getGoalTypesRequest();
    },
    async getSuggestedMonthlySavings(_, payload) {
      const { options, ...requestPayload } = payload || {};
      return await requests.getSuggestedMonthlySavingsRequest(
        requestPayload,
        options
      );
    },
    async calculateGoalAchieveDate(_, payload) {
      const { options, ...requestPayload } = payload || {};
      return await requests.calculateGoalAchieveDateRequest(
        requestPayload,
        options
      );
    },
  },
  getters: {
    dashboardGoals(store) {
      const data = store.dashboardGoals;
      return {
        ...data,
        ef_set: data.goals_ef?.ef,
        slider_array: data.slider_array,
        savings: data.savings,
        ef_allocation: data.goals_ef?.allocation,
        available: data.goals_ef?.available,
        available_desc: data.goals_ef?.available_desc,
        graph_data: data.graph?.graph_data,
        graph_cat: data.graph?.graph_categories,
        total_invested: data.total_invested,
        age_ap: data.graph?.age,
      };
    },
    plan(store) {
      return store.plan;
    },
    efSet(store) {
      return store.plan.goals_ef?.ef;
    },
  },
};
export default goalsStore;
